import React, { FC, useState } from 'react';
import * as actions from '~actions';
import ClaimDeviceV2 from '~Components/ClaimDevice';
import { ModalWrapper } from '~Components/ClaimDevice/styles';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useStorage } from '~Components/Storage';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import NumberInput from '~ComponentsImproved/Numpad';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import ClaimDevice from '~Scenes/Onboarding/Components/ClaimDevice';
import { Button } from '~Styles';

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const DeviceSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();

  const deviceHandler = useDeviceHandler();
  const dispatch = useDispatch();
  const modal = useModal();
  const logger = useLogger('settings-v2');
  const companyFlags = useSelector((state) => state.companyFlags);

  const [storage] = useStorage();

  const deviceNameToShow = companyFlags.useNewLicenses
    ? deviceHandler.activeDevice?.name
    : storage.taxComplianceDeviceName;

  const [showPasswordInput, setShowPasswordInput] = useState<boolean>(false);

  const button = useButton({
    onClick: () => {
      if (deviceNameToShow) {
        setShowPasswordInput(true);
      } else {
        modal.toggle();
      }
    },
  });

  return (
    <div>
      {Boolean(deviceNameToShow) && (
        <Paragraph
          level={isHandy ? 5 : 4}
          color="var(--colorBlueGray200)"
          bottom={isHandy ? 8 : 16}>
          {i18n('Settings.Device.Current')}: <strong>{deviceNameToShow}</strong>
        </Paragraph>
      )}
      <Button {...button} size={isHandy ? 'small' : 'medium'}>
        {deviceNameToShow ? i18n('Settings.Device.Change') : i18n('ClaimDevice.ClaimFirstDevice')}
      </Button>

      <Modal isShowing={modal.isShowing} hide={() => modal.toggle()}>
        <ModalWrapper
          display="flex"
          flexDirection="column"
          gap="medium"
          alignItems="center"
          justifyContent="center"
          padding={isHandy ? 'small' : '3xl'}
          marginTop="small"
          marginBottom="small">
          {companyFlags.useNewLicenses ? (
            <ClaimDeviceV2
              isHandy={isHandy}
              isChangeOfDevice
              onComplete={() => {
                modal.toggle();
                dispatch(actions.addToast(i18n('Onboarding.Device.Success'), true));
              }}
            />
          ) : (
            <ClaimDevice
              isHandy={isHandy}
              onComplete={() => {
                modal.toggle();
                dispatch(actions.addToast(i18n('Onboarding.Device.Success'), true));
              }}
            />
          )}
        </ModalWrapper>
      </Modal>

      {showPasswordInput && (
        <NumberInput
          onClose={() => setShowPasswordInput(false)}
          onSubmit={(value) => {
            if (value !== '1111') {
              logger.info('Tried to change device without correct password');
              dispatch(actions.addToast(i18n('Settings.NotAllowed'), true, 'notallowed', true));
              setShowPasswordInput(false);
            } else {
              modal.toggle();
              setShowPasswordInput(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default DeviceSettings;
